import { isSessionMarkerEnabled } from 'configureApi';
import AuthData from 'lib/authData';

/**
 * CD-8740 — the `has_session` marker cookie.
 *
 * oso-api sets this alongside the real session cookies. It is **not** HttpOnly
 * and carries no secret, so the SPA can read it synchronously at boot; its only
 * purpose is to let an anonymous visitor skip the `PUT /sessions` boot probe and
 * make **zero** network calls before first paint.
 *
 * Three rules govern everything in this module:
 *
 * 1. **The marker is a hint, never authority.** No component may read it as
 *    "the user is signed in". The only consumer is the boot-probe decision, and
 *    every path that branches on it still has to handle a 401.
 * 2. **Fail open.** The marker may only ever *suppress* a probe, never cause
 *    one. A marker that is missing when a session is in fact valid is a silent,
 *    unexplainable logout; a marker that is present when there is no session
 *    costs one wasted round trip and self-corrects. When in any doubt — the
 *    mechanism is disabled, cookies are unreadable, anything throws — we probe.
 * 3. **It is only readable where the SPA and API share a registrable domain.**
 *    The session cookies are host-only on the API host, so oso-api gives the
 *    marker an explicit `Domain` (`AUTH_COOKIE_MARKER_DOMAIN`). On academy there
 *    is no shared registrable domain and the mechanism is off entirely, so the
 *    probe there is unconditional — see `isSessionMarkerEnabled`.
 */
export const SESSION_MARKER_COOKIE = 'has_session';

const EXPIRED = 'Thu, 01 Jan 1970 00:00:00 GMT';

/**
 * Is the marker cookie present on this document?
 *
 * Matched on the exact cookie name — `has_session_other` and `x_has_session`
 * must not satisfy it, the same prefix-matching trap that had to be closed at
 * the gateway and in oso-api's cookie extraction.
 */
export const hasSessionMarker = (): boolean => {
  try {
    return document.cookie
      .split(';')
      .some((cookie) => cookie.split('=')[0].trim() === SESSION_MARKER_COOKIE);
  } catch (_e) {
    // Cookies unreadable (sandboxed frame, blocked storage). Treat as "unknown".
    return false;
  }
};

/**
 * The `Set-Cookie` strings needed to delete the marker from this host.
 *
 * oso-api sets the marker with an explicit `Domain` in deployed environments and
 * host-only locally, and the SPA is never told which. A deletion whose `Domain`
 * does not match the set is a **silent no-op**, so we emit one deletion per
 * plausible scope: host-only, then each parent domain (with and without the
 * leading dot).
 *
 * Only the single last label (`org`, `uk`) is skipped. A multi-label public
 * suffix is not recognised: for `cms.lumin-dr.co.uk` this still emits
 * `Domain=co.uk` and `Domain=.co.uk`. Browsers reject a `Domain` naming a public
 * suffix, so those two are harmless no-ops, and they could never have been the
 * marker's `Domain` either. Skipping them properly needs the Public Suffix List,
 * which is not worth shipping for a best-effort deletion.
 *
 * Exported for testing; prefer `clearSessionMarker`.
 */
export const markerDeletionCookies = (hostname: string): string[] => {
  const base = `${SESSION_MARKER_COOKIE}=; Path=/; Expires=${EXPIRED}; Max-Age=0`;
  const cookies = [base];

  const labels = hostname.split('.');
  // Stop one label short of the last, so a single-label TLD (`org`, `uk`) is never
  // targeted. A multi-label public suffix (`co.uk`) is NOT detected and is still
  // emitted; browsers reject it, so it is a harmless no-op (see the docstring).
  for (let i = 1; i < labels.length - 1; i += 1) {
    const domain = labels.slice(i).join('.');
    cookies.push(`${base}; Domain=${domain}`);
    cookies.push(`${base}; Domain=.${domain}`);
  }

  return cookies;
};

/**
 * Best-effort client-side removal of the marker, used when the probe 401s (a
 * stale marker) and on explicit logout.
 *
 * Best-effort is sufficient: if it fails, the next boot probes, gets a 401 and
 * renders the unauthenticated shell — one wasted round trip, no redirect loop.
 */
export const clearSessionMarker = (): void => {
  try {
    markerDeletionCookies(window.location.hostname).forEach((cookie) => {
      document.cookie = cookie;
    });
  } catch (_e) {
    // Cookies unwritable. The probe is the backstop.
  }
};

/**
 * Should the SPA issue the `PUT /sessions` boot probe?
 *
 * Returns `false` only for a browser we are confident is anonymous: the marker
 * mechanism is active in this environment, no marker cookie is present, **and**
 * no cached user profile survives from a previous session. Anything else — and
 * any error — probes.
 */
export const shouldProbeSession = (): boolean => {
  let mechanismEnabled: boolean;
  try {
    mechanismEnabled = isSessionMarkerEnabled();
  } catch (_e) {
    return true;
  }

  if (!mechanismEnabled) return true;

  try {
    if (document.cookie === undefined) return true;
  } catch (_e) {
    return true;
  }

  if (hasSessionMarker()) return true;

  // A cached profile means this browser believed it was signed in. Trusting an
  // absent marker over that would be the silent logout described above, so the
  // cache can only ever add a probe — never remove one.
  try {
    if (AuthData.get()) return true;
  } catch (_e) {
    return true;
  }

  return false;
};
